import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '@/utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true); // Default true = failsafe

  // Refresh session periodically (every 6 hours) to keep user logged in
  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const response = await API.post('/auth/refresh');
      // Update localStorage with the new session token
      if (response.data && response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        console.log('Session refreshed and token updated');
      } else {
        console.log('Session refreshed');
      }
    } catch (error) {
      // Session might be invalid - will be handled by checkAuth
      console.warn('Session refresh failed:', error);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
    
    // Set up periodic session refresh (every 6 hours)
    const refreshInterval = setInterval(refreshSession, 6 * 60 * 60 * 1000);
    
    // Also refresh on tab/window focus (user returning after being away)
    const handleFocus = () => {
      const token = localStorage.getItem('session_token');
      if (token) {
        refreshSession();
      }
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshSession]);

  // Helper to check if demo mode is active (from localStorage)
  const isDemoModeActive = () => localStorage.getItem('forgetmenot_demo_mode') === 'true';

  const checkAuth = async () => {
    // Skip auth check if demo mode is active
    if (isDemoModeActive()) {
      setLoading(false);
      setIsAuthenticated(false); // Demo mode handles its own "auth"
      return;
    }
    
    const token = localStorage.getItem('session_token');
    
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await API.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
      
      // NOTE: Don't refresh session immediately after auth check
      // The periodic refresh and on-focus refresh will handle session extension
      // Calling refresh here causes token mismatch issues
      
      // Fetch onboarding state
      try {
        const settingsResponse = await API.get('/settings');
        setOnboardingCompleted(settingsResponse.data.onboarding_completed ?? true);
      } catch {
        // Failsafe: if settings fail, assume onboarding complete
        setOnboardingCompleted(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await API.post('/auth/login', { email, password });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      // Return the 2FA data so the login page can redirect
      return {
        requires_2fa: true,
        pending_token: response.data.pending_token,
        totp_enabled: response.data.totp_enabled,
        email_2fa_enabled: response.data.email_2fa_enabled
      };
    }
    
    const { user: userData, session_token } = response.data;
    
    localStorage.setItem('session_token', session_token);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Check onboarding state for existing user
    try {
      const settingsResponse = await API.get('/settings');
      setOnboardingCompleted(settingsResponse.data.onboarding_completed ?? true);
    } catch {
      setOnboardingCompleted(true); // Failsafe
    }
    
    // Return session token for biometric storage
    return { user: userData, session_token };
  };

  // Direct login (for use after 2FA verification)
  const loginDirect = (userData, sessionToken) => {
    localStorage.setItem('session_token', sessionToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  // Login with stored session token (for biometric login)
  const loginWithSession = async (sessionToken) => {
    try {
      // Set the token first so the API call uses it
      localStorage.setItem('session_token', sessionToken);
      
      // Verify the session is still valid
      const response = await API.get('/auth/me');
      
      setUser(response.data);
      setIsAuthenticated(true);
      
      // Fetch onboarding state
      try {
        const settingsResponse = await API.get('/settings');
        setOnboardingCompleted(settingsResponse.data.onboarding_completed ?? true);
      } catch {
        setOnboardingCompleted(true);
      }
      
      return { success: true, user: response.data };
    } catch (error) {
      // Session is invalid, clear it
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
      return { success: false, error: 'Session expired' };
    }
  };

  const register = async (name, email, password) => {
    const response = await API.post('/auth/register', { name, email, password });
    const { user: userData, session_token } = response.data;
    
    localStorage.setItem('session_token', session_token);
    setUser(userData);
    setIsAuthenticated(true);
    setOnboardingCompleted(false); // New user needs onboarding
    
    return userData;
  };

  // Password reset methods
  const forgotPassword = async (email) => {
    const response = await API.post('/auth/forgot-password', { email });
    return response.data;
  };

  const resetPassword = async (token, newPassword) => {
    const response = await API.post('/auth/reset-password', { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  };

  const verifyResetToken = async (token) => {
    const response = await API.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  };

  const completeOnboarding = async () => {
    try {
      await API.put('/settings', { onboarding_completed: true });
      setOnboardingCompleted(true);
    } catch (error) {
      console.error('Failed to save onboarding state:', error);
      // Still mark as complete in state - failsafe
      setOnboardingCompleted(true);
    }
  };

  const resetOnboarding = async () => {
    try {
      await API.put('/settings', { onboarding_completed: false });
      setOnboardingCompleted(false);
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
    }
  };

  const logout = async () => {
    try {
      await API.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      // Clear app lock state on logout
      localStorage.removeItem('app_lock_enabled');
      localStorage.removeItem('lock_on_background');
      localStorage.removeItem('app_last_active');
      localStorage.removeItem('biometric_credential_id');
      localStorage.removeItem('biometric_enrolled');
      // Clear biometric login state
      localStorage.removeItem('biometric_login_enabled');
      localStorage.removeItem('biometric_user_id');
      localStorage.removeItem('biometric_credentials');
      setUser(null);
      setIsAuthenticated(false);
      setOnboardingCompleted(true); // Reset to failsafe
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    onboardingCompleted,
    login,
    register,
    logout,
    checkAuth,
    completeOnboarding,
    resetOnboarding,
    forgotPassword,
    resetPassword,
    verifyResetToken,
    loginDirect,  // Direct login after 2FA verification
    loginWithSession,  // Login with stored session (for biometric)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
